<template>
    <a11y-dialog 
        :id="`clone-confirm-${uuid}`" 
        dialog-root="#vue-dialog-root" 
        @dialog-ref="assignDialogRef" 
        :class-names="{ container: 'dialog', overlay: 'fe-modal-overlay' }">

        <div class="dialog-content fe-modal">
            <button type="button" 
                data-a11y-dialog-hide 
                @click="handleClose" 
                @keydown.enter="handleClose"
                aria-label="Close this dialog window" 
                class="fe-modal-close">
                &times;
            </button>
            <div v-if="project" class="u-padd-all">
                <h3 class="h h-sm u-mrg-btm-half u-mrg-top-none">Clone Project</h3>
                
                <template v-if="cloneDialogState === 'initial'">
                    <p>
                        A new copy of your project titled, "{{ project.siteName }}" will be created.
                    </p>
                    <div class="u-flex u-flex--justify-center u-flex--gap-base u-flex--wrap u-mrg-top">
                        <button @click="confirmClone" class="btn btn--primary">
                            Clone
                        </button>
                        <button @click="handleClose" class="btn btn--secondary">
                            Cancel
                        </button>
                    </div>
                </template>
                
                <template v-else-if="cloneDialogState === 'cloning'">
                    <p>Please wait while a copy of your project "{{ project.siteName }}" is created.</p>
                    <div class="u-flex u-flex--justify-center u-mrg-top">
                        <i class="fa fa-spinner fa-spin"></i>
                    </div>
                </template>
                
                <template v-else-if="cloneDialogState === 'success'">
                    <p>
                        <i class="fa fa-check-circle status-done-icon"></i> New project: "{{ newProjectInfo?.name }}" created successfully!
                    </p>
                    <p>
                        A new project has been created and added to your saved projects list.
                    </p>
                    <div class="u-flex u-flex--justify-center u-mrg-top">
                        <button @click="handleClose" class="btn btn--primary">
                            Close
                        </button>
                    </div>
                </template>
                
                <template v-else-if="cloneDialogState === 'error'">
                    <p>
                        Unable to copy project, "{{ project.siteName }}".
                    </p>
                    <p class="u-color-error">
                        <i class="fa fa-exclamation-triangle u-color-error status-error-icon"></i> {{ cloneMessage }}
                    </p>
                    <div class="u-flex u-flex--justify-center u-flex--gap-base u-flex--wrap u-mrg-top">
                        <button @click="confirmClone" class="btn btn--primary">
                            Try Again
                        </button>
                        <button @click="handleClose" class="btn btn--secondary">
                            Close
                        </button>
                    </div>
                </template>
            </div>
        </div>
    </a11y-dialog>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import axios from "axios"

const props = defineProps({
    project: {
        type: Object,
        default: null
    },
    visible: {
        type: Boolean,
        default: false
    },
    cloneUrl: {
        type: String,
        required: true
    }
})

const emit = defineEmits(['close', 'clone-success', 'clone-error'])

// Dialog state
const dialog = ref(null)
const uuid = ref(`clone-dialog-${Date.now()}-${Math.floor(Math.random() * 1000000)}`)
const cloneDialogState = ref('initial') // 'initial', 'cloning', 'success', 'error'
const cloneMessage = ref('')
const newProjectInfo = ref(null)

// Watch for visibility changes
watch(() => props.visible, (newVisible) => {
    if (newVisible && dialog.value) {
        dialog.value.show()
    } else if (!newVisible && dialog.value) {
        dialog.value.hide()
    }
})

// Watch for project changes to reset state
watch(() => props.project, () => {
    resetState()
})

function resetState() {
    cloneDialogState.value = 'initial'
    cloneMessage.value = ''
    newProjectInfo.value = null
}

function handleClose() {
    resetState()
    emit('close')
}

function assignDialogRef(dialogRef) {
    if (dialogRef) {
        dialog.value = dialogRef
        dialog.value.on('hide', handleClose)
        dialog.value.on('destroy', () => dialog.value.off('hide', handleClose))
    }
}

async function confirmClone() {
    if (!props.project) return
    
    console.log("Confirming clone for project:", props.project.sfId)
    cloneDialogState.value = 'cloning'
    cloneMessage.value = ''
    newProjectInfo.value = null
    
    try {
        // Create form data for POST request
        const formData = new FormData()
        formData.append('userQuoteGuid', props.project.sfId)
        
        const response = await axios.post(props.cloneUrl, formData)
        console.log("Clone response:", response.data)
        
        if (response.data && response.data.success) {
            console.log("Clone successful")
            cloneDialogState.value = 'success'
            
            // Extract new project information
            if (response.data.result && response.data.result.newQuote) {
                newProjectInfo.value = {
                    id: response.data.result.newQuote.Id,
                    name: response.data.result.newQuote.Name
                }
            }
            
            // Emit success event
            emit('clone-success', {
                originalProject: props.project,
                newProject: newProjectInfo.value
            })
        } else {
            console.error("Clone failed - server returned success: false")
            cloneDialogState.value = 'error'
            cloneMessage.value = response.data.errorMessage || 'Clone operation failed.'
            
            emit('clone-error', {
                project: props.project,
                error: cloneMessage.value
            })
        }
    } catch (error) {
        console.error("Error cloning project:", error)
        cloneDialogState.value = 'error'
        cloneMessage.value = 'An unexpected error occurred while cloning the project.'
        
        emit('clone-error', {
            project: props.project,
            error: cloneMessage.value
        })
    }
}

onMounted(() => {
    // Assign dialog to window for debugging purposes
    window.kbMaxCloneDialog = {
        dialog, 
        cloneDialogState, 
        newProjectInfo,
        cloneMessage,
        newProjectInfo
    }
})
</script>

<style scoped lang="scss">
@use "@styles/vars/_colors" as *;
.status-done-icon {
    color: $color-green-md;
    margin-right: 0.25em;
}
.status-error-icon {
    color: $color-error;
    margin-right: 0.25em;
}
</style>
